/**
 * AI Helper Panel — used inside ProjectDetailModal.
 * Provides 5 AI features powered by Emergent LLM Key (Gemini).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles,
  Wand2,
  Search,
  ListChecks,
  ShieldCheck,
  Cable,
  Loader2,
  ChevronDown,
  X,
  Plus,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRIORITY_COLORS = {
  low: 'bg-zinc-100 text-zinc-700',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-red-100 text-red-700',
};

// ==================== Wizard Dialog ====================
function WizardDialog({ open, project, onClose, onItemsCreated }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState(30);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!prompt.trim()) {
      toast.error('Layihə təsviri daxil edin');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/ai/project-wizard`,
        {
          prompt: prompt.trim(),
          target_duration_days: Number(duration) || 30,
          project_id: project?.id,
        },
        { headers, timeout: 60000 }
      );
      setResult(r.data);
      if (r.data.persisted) {
        toast.success(`${r.data.created_items?.length || 0} mərhələ yaradıldı`);
        onItemsCreated?.();
      } else {
        toast.info(`${r.data.stages?.length || 0} mərhələ təklif olundu`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI sorğusu uğursuz');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="ai-wizard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-purple-600" />
            AI Layihə Wizard
          </DialogTitle>
          <DialogDescription>
            Təbii dildə layihənin təsvirini yazın — Gemini mərhələləri avtomatik generasiya edəcək.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium">Layihə təsviri</label>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Məs: 5 binadan ibarət yaşayış kompleksinə FTTH şəbəkə çəkilməsi..."
              rows={5}
              data-testid="wizard-prompt-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Hədəf müddət (gün)</label>
            <Input
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="max-w-[160px]"
              data-testid="wizard-duration-input"
            />
          </div>
        </div>

        {result && (
          <div className="mt-3 border rounded-lg p-3 bg-purple-50 max-h-72 overflow-y-auto">
            {result.project_name && (
              <div className="text-sm font-semibold mb-1">{result.project_name}</div>
            )}
            {result.summary && (
              <div className="text-xs text-zinc-700 mb-2">{result.summary}</div>
            )}
            <div className="text-xs font-semibold text-zinc-700 mb-1">
              Mərhələlər ({result.stages?.length || 0}):
            </div>
            <ol className="space-y-1.5 list-decimal list-inside text-xs">
              {(result.stages || []).map((s, idx) => (
                <li key={idx} className="bg-white rounded p-1.5">
                  <div className="font-medium inline">{s.title}</div>{' '}
                  <Badge className={`${PRIORITY_COLORS[s.priority] || ''} text-[10px] ml-1`}>
                    {s.priority}
                  </Badge>
                  <div className="text-zinc-500 mt-0.5">{s.description}</div>
                  <div className="text-zinc-400 mt-0.5">
                    Başlanma: +{s.offset_start_days} gün • Müddət: {s.duration_days} gün
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Bağla
          </Button>
          <Button
            onClick={run}
            disabled={loading}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="wizard-run-btn"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-1" />
            )}
            {project?.id ? 'Mərhələlər yarat' : 'Önizləmə'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== KMZ Discovery Dialog ====================
function DiscoveryDialog({ open, project, onClose }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/ai/kmz-discovery`,
        { project_id: project.id, max_suggestions: 7 },
        { headers, timeout: 60000 }
      );
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI sorğusu uğursuz');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (open) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="ai-discovery-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-5 w-5 text-purple-600" />
            KMZ Discovery
          </DialogTitle>
          <DialogDescription>
            İdxal edilmiş geo elementlərdən AI ilə tapşırıq təklifləri
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-zinc-600 py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            Gemini geo məlumatlarını analiz edir...
          </div>
        )}

        {result && (
          <div className="max-h-96 overflow-y-auto space-y-2">
            {(result.suggestions || []).map((s, idx) => (
              <div key={idx} className="border rounded p-2.5 bg-white">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-medium flex-1">{s.title}</div>
                  <Badge className={`${PRIORITY_COLORS[s.priority] || ''} text-[10px]`}>
                    {s.priority}
                  </Badge>
                </div>
                {s.description && (
                  <div className="text-xs text-zinc-600 mt-1">{s.description}</div>
                )}
                {s.reason && (
                  <div className="text-[11px] text-purple-700 mt-1 italic">{s.reason}</div>
                )}
                {(s.related_geo_names || []).length > 0 && (
                  <div className="flex gap-1 flex-wrap mt-1">
                    {(s.related_geo_names || []).slice(0, 6).map((n, i) => (
                      <Badge key={i} variant="outline" className="text-[10px]">
                        {n}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {(result.suggestions || []).length === 0 && (
              <div className="text-sm text-zinc-500">Təklif tapılmadı</div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Bağla
          </Button>
          {!loading && (
            <Button onClick={run} variant="outline" data-testid="discovery-rerun-btn">
              <Sparkles className="h-4 w-4 mr-1" />
              Yenidən sorğula
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== Network Validator Dialog ====================
function ValidatorDialog({ open, project, onClose }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/ai/network-validator`,
        { project_id: project.id },
        { headers, timeout: 60000 }
      );
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI sorğusu uğursuz');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (open) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="ai-validator-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-purple-600" />
            Şəbəkə Validatoru
          </DialogTitle>
          <DialogDescription>
            Geo elementlər arasında topology yoxlanışı + AI tövsiyələri
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-zinc-600 py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            Şəbəkə analiz edilir...
          </div>
        )}

        {result && (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            <div className="grid grid-cols-3 gap-2">
              <StatBox label="Qüllələr" value={result.total_points} color="bg-blue-50 text-blue-700" />
              <StatBox label="Kabellər" value={result.total_lines} color="bg-emerald-50 text-emerald-700" />
              <StatBox
                label="Problem"
                value={result.issue_count}
                color={result.issue_count > 0 ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}
              />
            </div>

            {result.summary && (
              <div className="border rounded p-2.5 bg-purple-50">
                <div className="text-xs font-semibold text-purple-800 mb-1">AI Xülasə:</div>
                <div className="text-xs text-zinc-800">{result.summary}</div>
              </div>
            )}

            {(result.issues || []).length > 0 && (
              <div className="border rounded">
                <div className="text-xs font-semibold px-2.5 py-1.5 bg-red-50 border-b text-red-800">
                  Tapılan problemlər
                </div>
                <div className="divide-y">
                  {result.issues.map((iss, idx) => (
                    <div key={idx} className="p-2.5 text-xs">
                      <div className="flex items-center gap-2">
                        <Badge variant="destructive" className="text-[10px]">
                          {iss.type}
                        </Badge>
                        <span className="font-medium">{iss.geo_name}</span>
                      </div>
                      <div className="text-zinc-600 mt-1">{iss.detail}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(result.recommendations || []).length > 0 && (
              <div className="border rounded p-2.5 bg-amber-50">
                <div className="text-xs font-semibold text-amber-800 mb-1">Tövsiyələr:</div>
                <ul className="text-xs space-y-1 list-disc list-inside">
                  {result.recommendations.map((r, idx) => (
                    <li key={idx}>{r}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Bağla
          </Button>
          {!loading && (
            <Button onClick={run} variant="outline" data-testid="validator-rerun-btn">
              <Sparkles className="h-4 w-4 mr-1" />
              Yenidən
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== Cable Calculator Dialog ====================
function CableDialog({ open, project, onClose }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [cableType, setCableType] = useState('fiber-optic');
  const [costPerMeter, setCostPerMeter] = useState(1.2);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/ai/cable-calculator`,
        {
          project_id: project.id,
          cable_type: cableType,
          cost_per_meter: Number(costPerMeter) || 0,
        },
        { headers, timeout: 60000 }
      );
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI sorğusu uğursuz');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="ai-cable-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cable className="h-5 w-5 text-purple-600" />
            Kabel Hesablayıcı
          </DialogTitle>
          <DialogDescription>
            Bütün LineString-lərin Haversine cəm uzunluğu + AI xərc xülasəsi
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Kabel tipi</label>
              <Input
                value={cableType}
                onChange={(e) => setCableType(e.target.value)}
                placeholder="fiber-optic, copper..."
                data-testid="cable-type-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Vahid qiymət (AZN/m)</label>
              <Input
                type="number"
                step="0.01"
                value={costPerMeter}
                onChange={(e) => setCostPerMeter(e.target.value)}
                data-testid="cable-cost-input"
              />
            </div>
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-sm text-zinc-600 py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              Hesablanır...
            </div>
          )}

          {result && (
            <div className="border rounded p-3 bg-emerald-50 space-y-2 max-h-72 overflow-y-auto">
              <div className="grid grid-cols-3 gap-2 text-center">
                <StatBox
                  label="Cəmi uzunluq"
                  value={`${result.total_length_km} km`}
                  color="bg-blue-50 text-blue-700"
                />
                <StatBox
                  label="Xətt sayı"
                  value={result.line_count}
                  color="bg-zinc-100 text-zinc-700"
                />
                <StatBox
                  label="Cəmi xərc"
                  value={`${result.total_cost} AZN`}
                  color="bg-emerald-100 text-emerald-700"
                />
              </div>
              {result.summary && (
                <div className="text-xs text-zinc-800 border-t pt-2">{result.summary}</div>
              )}
              {(result.breakdown || []).length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-purple-700">
                    Detallı parçalanış ({result.breakdown.length})
                  </summary>
                  <div className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                    {result.breakdown.map((b) => (
                      <div key={b.geo_id} className="flex justify-between border-b py-0.5">
                        <span className="truncate">{b.name}</span>
                        <span className="text-zinc-500">{b.length_m} m</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Bağla
          </Button>
          <Button
            onClick={run}
            disabled={loading}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="cable-run-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
            Hesabla
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== Geo Task Gen Dialog ====================
function GeoTaskDialog({ open, project, polygons, onClose, onItemsCreated }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [selectedId, setSelectedId] = useState(polygons?.[0]?.id || '');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);

  React.useEffect(() => {
    if (open) {
      setSelectedId(polygons?.[0]?.id || '');
      setResult(null);
    }
  }, [open, polygons]);

  const run = async () => {
    if (!selectedId) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/ai/geo-task-gen`,
        { project_id: project.id, geo_item_id: selectedId },
        { headers, timeout: 60000 }
      );
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI sorğusu uğursuz');
    } finally {
      setLoading(false);
    }
  };

  const createItems = async () => {
    if (!result?.tasks) return;
    setCreating(true);
    try {
      let count = 0;
      for (const t of result.tasks) {
        await axios.post(
          `${API}/projects/${project.id}/items`,
          {
            title: t.title,
            description: t.description,
            priority: ['low', 'medium', 'high'].includes(t.priority) ? t.priority : 'medium',
            tags: ['ai-geo', result.zone_name].filter(Boolean),
          },
          { headers }
        );
        count++;
      }
      toast.success(`${count} mərhələ əlavə edildi`);
      onItemsCreated?.();
      onClose();
    } catch (e) {
      toast.error('Mərhələlər yaradılmadı');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="ai-geo-task-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-purple-600" />
            Geo Tapşırıq Generasiyası
          </DialogTitle>
          <DialogDescription>
            Polygon zonasından sahə işləri üçün konkret tapşırıqlar
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium">Zonanı seçin (Polygon)</label>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm"
              data-testid="geo-task-polygon-select"
            >
              <option value="">— seçin —</option>
              {(polygons || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name || '(adsız)'}
                </option>
              ))}
            </select>
            {(polygons || []).length === 0 && (
              <div className="text-xs text-zinc-500 italic mt-1">
                Bu layihədə hələ Polygon yoxdur
              </div>
            )}
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-sm text-zinc-600 py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              Generasiya edilir...
            </div>
          )}

          {result && (
            <div className="border rounded p-3 bg-purple-50">
              <div className="text-sm font-semibold mb-2">Zona: {result.zone_name}</div>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {(result.tasks || []).map((t, idx) => (
                  <div key={idx} className="bg-white rounded p-2">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium flex-1">{t.title}</div>
                      <Badge className={`${PRIORITY_COLORS[t.priority] || ''} text-[10px]`}>
                        {t.priority}
                      </Badge>
                      <span className="text-[11px] text-zinc-500">{t.estimated_hours} saat</span>
                    </div>
                    {t.description && (
                      <div className="text-xs text-zinc-600 mt-1">{t.description}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Bağla
          </Button>
          {result?.tasks?.length > 0 && (
            <Button
              onClick={createItems}
              disabled={creating}
              variant="outline"
              data-testid="geo-task-create-items-btn"
            >
              {creating ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-1" />
              )}
              Mərhələlərə əlavə et
            </Button>
          )}
          <Button
            onClick={run}
            disabled={loading || !selectedId}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="geo-task-run-btn"
          >
            <Sparkles className="h-4 w-4 mr-1" />
            Generasiya et
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== Helper component ====================
function StatBox({ label, value, color }) {
  return (
    <div className={`rounded p-2 ${color}`}>
      <div className="text-lg font-bold">{value}</div>
      <div className="text-[10px] uppercase opacity-70">{label}</div>
    </div>
  );
}

// ==================== Main Panel ====================
export default function ProjectAIPanel({ project, polygons, onItemsCreated }) {
  const [wizardOpen, setWizardOpen] = useState(false);
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  const [validatorOpen, setValidatorOpen] = useState(false);
  const [cableOpen, setCableOpen] = useState(false);
  const [geoTaskOpen, setGeoTaskOpen] = useState(false);

  return (
    <div data-testid="ai-panel" className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-5 w-5 text-purple-600" />
        <div className="text-sm font-bold text-purple-900">AI Assistants</div>
        <Badge className="bg-purple-200 text-purple-800 text-[10px]">Gemini</Badge>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
        <Button
          variant="outline"
          size="sm"
          className="bg-white justify-start"
          onClick={() => setWizardOpen(true)}
          data-testid="ai-wizard-btn"
        >
          <Wand2 className="h-3.5 w-3.5 mr-1.5 text-purple-600" />
          <span className="text-xs">Layihə Wizard</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="bg-white justify-start"
          onClick={() => setDiscoveryOpen(true)}
          data-testid="ai-discovery-btn"
        >
          <Search className="h-3.5 w-3.5 mr-1.5 text-purple-600" />
          <span className="text-xs">KMZ Discovery</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="bg-white justify-start"
          onClick={() => setGeoTaskOpen(true)}
          data-testid="ai-geo-task-btn"
          disabled={!polygons?.length}
          title={!polygons?.length ? 'Polygon yoxdur' : ''}
        >
          <ListChecks className="h-3.5 w-3.5 mr-1.5 text-purple-600" />
          <span className="text-xs">Geo Task Gen</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="bg-white justify-start"
          onClick={() => setValidatorOpen(true)}
          data-testid="ai-validator-btn"
        >
          <ShieldCheck className="h-3.5 w-3.5 mr-1.5 text-purple-600" />
          <span className="text-xs">Şəbəkə Validatoru</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="bg-white justify-start"
          onClick={() => setCableOpen(true)}
          data-testid="ai-cable-btn"
        >
          <Cable className="h-3.5 w-3.5 mr-1.5 text-purple-600" />
          <span className="text-xs">Kabel Hesablayıcı</span>
        </Button>
      </div>

      <WizardDialog
        open={wizardOpen}
        project={project}
        onClose={() => setWizardOpen(false)}
        onItemsCreated={onItemsCreated}
      />
      <DiscoveryDialog
        open={discoveryOpen}
        project={project}
        onClose={() => setDiscoveryOpen(false)}
      />
      <ValidatorDialog
        open={validatorOpen}
        project={project}
        onClose={() => setValidatorOpen(false)}
      />
      <CableDialog
        open={cableOpen}
        project={project}
        onClose={() => setCableOpen(false)}
      />
      <GeoTaskDialog
        open={geoTaskOpen}
        project={project}
        polygons={polygons}
        onClose={() => setGeoTaskOpen(false)}
        onItemsCreated={onItemsCreated}
      />
    </div>
  );
}
